import useUser from "../hooks/useUser";
import useCrew from "../hooks/useCrew";
import useUserRoles from "../hooks/useUserRoles";
import { COMPUTED_ROLES, PERMISSIONS } from "../constants";
import { MyInfoResponse } from "../api/user";

export interface UserRoles {
  isCrewAdmin: boolean;
  isSuperAdmin: boolean;
}

export interface Crew {
  id: number;
}

export interface AuthorizationHook {
  hasPermission: (permission: string) => boolean;
  hasRole: (roleCode: string) => boolean;
  hasAnyRole: (roleCodes: string[]) => boolean;
  hasAllRoles: (roleCodes: string[]) => boolean;
  user: MyInfoResponse | null;
  crew: Crew;
  userRoles: UserRoles;
}

/**
 * Check if user has a specific API role in the current crew
 */
const checkApiRole = (user: MyInfoResponse | null, crew: Crew, roleCode: string): boolean => {
  if (!user || !crew) return false;
  // Super admin has all roles
  if (crew.id === 0) return true;
  const currentCrew = user.crews?.find((c) => c.id === crew.id);
  return currentCrew?.roles?.some((role) => role.code === roleCode) || false;
};

/**
 * Check if user has a specific permission in the current crew
 */
const checkPermission = (user: MyInfoResponse | null, crew: Crew, permission: string): boolean => {
  if (!user || !crew) return false;
  // Super user check
  if (permission === PERMISSIONS.SUPER_USER && crew.id === 0) {
    return true;
  }
  const currentCrew = user.crews?.find((c) => c.id === crew.id);
  return currentCrew?.permissions?.some((perm) => perm.code === permission) || false;
};

export const useAuthorization = (): AuthorizationHook => {
  const user = useUser();
  const { crew } = useCrew();
  const userRoles = useUserRoles();

  const hasPermission = (permission: string): boolean => {
    return checkPermission(user, crew, permission);
  };

  const hasRole = (roleCode: string): boolean => {
    // Handle computed roles first
    switch (roleCode) {
      case COMPUTED_ROLES.CREW_ADMIN:
        return userRoles.isCrewAdmin;
      case COMPUTED_ROLES.SUPER_ADMIN:
        return userRoles.isSuperAdmin;
      default:
        return checkApiRole(user, crew, roleCode);
    }
  };

  const hasAnyRole = (roleCodes: string[]): boolean => {
    return roleCodes.some((roleCode: string) => hasRole(roleCode));
  };

  const hasAllRoles = (roleCodes: string[]): boolean => {
    return roleCodes.every((roleCode: string) => hasRole(roleCode));
  };

  return {
    hasPermission,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    user,
    crew,
    userRoles,
  };
};
